/**
 * 発注代行キュー（08 §10・FR-122・D-69）＝LP 卸向け 特長03「メーカーへの発注も、外注できる」の
 * **運営側の受け皿**。卸が C-21 から出した「運営に発注を外注」を受領→対応→完了記録する。
 *
 * ★ **発注者（職人）の氏名・電話・LINE 表示名はここに出ない**（D-63）＝§8 の伏字ではなく
 *   「API が返さない」で担保している。画面側で足そうとしても、そもそもデータが来ない。
 * ★ **取消済みの受注に紐づく依頼にはバッジを出す**（D-69⑧）＝取り消された注文の材料を
 *   そのままメーカーへ発注してしまう事故を防ぐ、この画面で一番大事な表示。
 * ★ 差戻しは M1 では作らない（電話・Slack で卸へ連絡 → 卸が取り消す）。
 */

import { ops, type OpsWorkRequest } from "@kenzai/api-client";
import { Badge, Card, ConfirmDialog, Empty, ErrorNote, formatDate, formatDateTime, PageHeader, Skeleton } from "@kenzai/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

const KIND_LABEL: Record<OpsWorkRequest["kind"], string> = {
  procure: "発注",
  ship: "発送",
  other: "その他",
};

const STATUS_LABEL: Record<OpsWorkRequest["status"], string> = {
  open: "未対応",
  in_progress: "対応中",
  done: "完了",
  cancelled: "依頼元が取消",
};

export function RequestQueue() {
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const [target, setTarget] = useState<{ row: OpsWorkRequest; next: "in_progress" | "done" } | null>(null);
  const [onlyOpen, setOnlyOpen] = useState(true);

  const list = useQuery({
    queryKey: ["ops-requests", onlyOpen],
    // ★「未対応だけ」にすると受領した瞬間に消えて完了できない＝**未完了（open＋対応中）**で絞る
    queryFn: () => ops.requests(onlyOpen ? { status: ["open", "in_progress"] } : {}),
    // 依頼は Slack でも飛ぶが、画面を開きっぱなしにする運用も想定して定期取得する
    refetchInterval: 60_000,
  });

  const patch = useMutation({
    mutationFn: (v: { id: number; updated_at: string; status: "in_progress" | "done"; reply_note?: string }) =>
      ops.patchRequest(v.id, { updated_at: v.updated_at, status: v.status, reply_note: v.reply_note }),
    onSuccess: () => {
      setError(null);
      setTarget(null);
      void qc.invalidateQueries({ queryKey: ["ops-requests"] });
    },
    onError: (e) => {
      // 失敗はダイアログを閉じてから知らせる（理由がモーダルの裏に回らないように）
      setTarget(null);
      setError(e);
    },
  });

  return (
    <>
      <PageHeader
        title="発注代行"
        subtitle="卸から届いた「メーカーへの発注をお願いしたい」の一覧です。受領して、対応が終わったら記録します。"
      />

      <ErrorNote error={error} />

      <label style={{ display: "inline-flex", alignItems: "center", gap: "var(--kz-space-2)", minHeight: 44 }}>
        <input
          type="checkbox"
          data-testid="only-open"
          checked={onlyOpen}
          onChange={(e) => setOnlyOpen(e.target.checked)}
          style={{ width: 20, height: 20 }}
        />
        未完了のものだけ表示
      </label>

      {list.isLoading ? (
        // 依頼はカードで並ぶ＝カードの**形**で待たせる（文字1行だと到着の瞬間に高さが跳ねる）
        <div style={{ display: "grid", gap: "var(--kz-space-3)", marginTop: "var(--kz-space-4)" }}>
          {[0, 1, 2].map((i) => (
            <Card key={i}>
              <Skeleton rows={3} height={16} />
            </Card>
          ))}
        </div>
      ) : list.isError ? (
        <ErrorNote error={list.error} />
      ) : (list.data?.items.length ?? 0) === 0 ? (
        <Empty title="依頼はありません" hint="卸から発注代行の依頼が届くとここに出ます。" />
      ) : (
        <div style={{ display: "grid", gap: "var(--kz-space-3)", marginTop: "var(--kz-space-4)" }}>
          {list.data!.items.map((r) => (
            <Card key={r.id} padding="0">
              <div
                data-testid={`ops-request-${r.id}`}
                style={{ padding: "var(--kz-space-4)", display: "grid", gap: "var(--kz-space-2)" }}
              >
                <div style={{ display: "flex", gap: "var(--kz-space-2)", flexWrap: "wrap", alignItems: "center" }}>
                  <Badge tone={r.status === "done" ? "ok" : r.status === "cancelled" ? "danger" : "warn"}>
                    {STATUS_LABEL[r.status]}
                  </Badge>
                  <Badge>{KIND_LABEL[r.kind]}</Badge>
                  <strong>{r.tenant_name}</strong>
                  {/* ★D-69⑧＝この画面で一番大事な表示 */}
                  {r.order_status === "取消" && (
                    <Badge tone="danger" data-testid={`ops-request-cancelled-order-${r.id}`}>
                      対象の注文は取消済み
                    </Badge>
                  )}
                  <span
                    className="kz-num"
                    style={{ marginLeft: "auto", fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}
                  >
                    {formatDateTime(r.created_at)}
                  </span>
                </div>

                <div style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
                  {r.order_display_no && <span className="kz-num">{r.order_display_no}</span>}
                  {r.customer_name && <>／{r.customer_name}</>}
                  {r.desired_date && <>／希望日 {formatDate(r.desired_date)}</>}
                </div>

                <p style={{ margin: 0, whiteSpace: "pre-wrap", lineHeight: 1.8 }}>{r.body}</p>

                {r.reply_note && (
                  <div
                    style={{
                      background: "var(--kz-color-surface-sunken)",
                      borderRadius: "var(--kz-radius-sm)",
                      padding: "var(--kz-space-3)",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {r.reply_note}
                  </div>
                )}

                {r.status !== "done" && r.status !== "cancelled" && (
                  <div style={{ display: "flex", gap: "var(--kz-space-2)", flexWrap: "wrap" }}>
                    {r.status === "open" && (
                      <button
                        type="button"
                        className="kz-btn kz-btn--flat"
                        data-testid={`ops-receive-${r.id}`}
                        onClick={() => setTarget({ row: r, next: "in_progress" })}
                      >
                        受け付ける
                      </button>
                    )}
                    <button
                      type="button"
                      className="kz-btn kz-btn--primary"
                      data-testid={`ops-complete-${r.id}`}
                      onClick={() => setTarget({ row: r, next: "done" })}
                    >
                      完了を記録する
                    </button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {target && (
        <ConfirmDialog
          title={target.next === "in_progress" ? "この依頼を受け付けますか？" : "この依頼を完了にしますか？"}
          body={
            target.next === "in_progress" ? (
              <>依頼元の卸には「対応中」と表示されます。</>
            ) : (
              <>
                対応した内容を記入してください。
                <strong>この記入は依頼元の卸にそのまま表示されます</strong>（完了を伝える唯一の手段です）。
                {target.row.order_status === "取消" && (
                  <>
                    <br />
                    <strong>※ 対象の注文は取り消されています。発注済みでないかご確認ください。</strong>
                  </>
                )}
              </>
            )
          }
          confirmLabel={target.next === "in_progress" ? "受け付ける" : "完了にする"}
          // 完了は対応内容の記入が必須＝`requireReason` の入力欄をそのまま使う
          requireReason={target.next === "done"}
          busy={patch.isPending}
          onConfirm={(reason) =>
            patch.mutate({
              id: target.row.id,
              updated_at: target.row.updated_at,
              status: target.next,
              reply_note: target.next === "done" ? reason : undefined,
            })
          }
          onCancel={() => setTarget(null)}
        />
      )}
    </>
  );
}
